/* eslint-disable max-len */
import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { Drawing } from './core/Drawing/Drawing';
import drawing from 'src/assets/images/drawing.jpg';
import { Topic, topics } from 'src/config/topics';
import { Button } from './shared/Button/Button';
import { Icon } from './shared/Icon/Icon';
import { useMediaQuery } from 'react-responsive';
import { Feedback } from './shared/Feedback/Feedback';
import { useToggle } from 'src/hooks/useToggle';
import { Modal } from './shared/Modal/Modal';
import { Drawer } from './shared/Drawer/Drawer';
import marked from 'marked';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import wethouder from 'src/assets/images/wethouder.jpg';

export type SelectedTopic = Topic & { content?: string } | null;

export type TopicContextType = [SelectedTopic, React.Dispatch<React.SetStateAction<SelectedTopic>>];

export const TopicContext = createContext<TopicContextType>([null, () => null]);

export const App: React.VFC = () => {
  const topicState = useState<SelectedTopic>(null);
  const [selectedTopic, setSelectedTopic] = topicState;
  const isCorrectDevice = useMediaQuery({ orientation: 'landscape', minDeviceWidth: 768 });
  const [modalOpen, toggleModal] = useToggle(true);

  const variant = useMemo(() => {
    switch (selectedTopic?.category) {
      case 'health':
        return 'primary';
      case 'tech':
        return 'secondary';
      default:
        return undefined;
    }
  }, [selectedTopic?.category]);

  const handleDrawerClose = useCallback(() => setSelectedTopic(null), [setSelectedTopic]);

  const reset = useCallback(() => {
    setSelectedTopic(null);
    toggleModal();
  }, [setSelectedTopic, toggleModal]);

  const noop = () => undefined;

  const getTopicMarkdown = useCallback(async () => {
    if (!selectedTopic) return;
    const { default: mdPath } = await import(`../data/${selectedTopic.markdown}`);
    const content = await (await fetch(mdPath)).text();

    setSelectedTopic({ ...selectedTopic, content });
  }, [selectedTopic, setSelectedTopic]);

  useEffect(() => {
    if (selectedTopic?.content) return;

    getTopicMarkdown();
  }, [getTopicMarkdown, selectedTopic?.content]);

  if (!isCorrectDevice) {
    return (
      <Feedback
        icon="DeviceMobileIcon"
        title="Een draai verwijderd?"
        description="Deze interactive wordt helaas niet ondersteund op mobiele telefoons of in landschapmodus. Zit je op een tablet? Draai je device om te de interactive te bekijken."
      />
    );
  }

  return (
    <TopicContext.Provider value={topicState}>
      <div className="absolute right-0 mt-6 mr-6 z-10 space-x-3">
        <Button
          tag="a"
          href={`https://www.linkedin.com/sharing/share-offsite/?url=${window.location.href}`}
          variant={variant}
          tooltip="LinkedIn"
          iconPadding
          newWindow
        >
          <Icon icon="ExternalLinkIcon" />
        </Button>
        <Button
          tag="a"
          href="https://technologievoorthuis.nl/over-ons/"
          variant={variant}
          tooltip="Technologie voor thuis"
          iconPadding
          newWindow
        >
          <Icon icon="InformationCircleIcon" />
        </Button>
        <CopyToClipboard text={window.location.href}>
          <Button
            tag="button"
            type="button"
            onClick={noop}
            variant={variant}
            tooltip="Kopieer naar plakboard"
            iconPadding
          >
            <Icon icon="LinkIcon" />
          </Button>
        </CopyToClipboard>
        <Button
          tag="a"
          href="https://www.dropbox.com/s/g0nnqx7fpziuet3/Droomplaat%20Zorg%26Tech_Den%20Haag.pdf?dl=0"
          variant={variant}
          tooltip="Download visual"
          newWindow
          iconPadding
        >
          <Icon icon="DownloadIcon" />
        </Button>
        <Button tag="button" onClick={reset} type="button" variant={variant} tooltip="Introductie" iconPadding>
          <Icon icon="HomeIcon" />
        </Button>
      </div>
      <div className="h-full flex">
        <Drawer isOpen={!!selectedTopic} onToggle={handleDrawerClose} variant={variant}>
          <h3>{selectedTopic?.title}</h3>
          <div className="s-markdown" dangerouslySetInnerHTML={{ __html: marked(selectedTopic?.content ?? '') }} />
        </Drawer>
        <div className="flex items-center flex-1 relative">
          <div className="absolute inset-0 flex items-center justify-center">
            <Drawing image={drawing} topics={topics} />
          </div>
        </div>
      </div>
      <Modal
        isOpen={modalOpen}
        onRequestClose={toggleModal}
        title="Samenbrengen sociaal domein en tech wereld"
        customClassname="text-twilight-dark"
      >
        <p>Als stadsbestuur van Den Haag zien wij enorme kansen in technologische vernieuwing om zorg en ondersteuning bereikbaar, betaalbaar en bemensbaar te houden voor iedereen. Zorginnovatie stimuleer ik dan ook van harte.</p>
        <h4>De veranderkracht van ondernemers</h4>
        <p>Een goede verbinding tussen de wereld van technologie en het sociale domein is daarbij essentieel. Ik geloof in de grote veranderkracht die ondernemers, investeerders en kennisinstellingen met zich meebrengen. Door hen te koppelen aan het sociale domein kunnen we zorg en ondersteuning slimmer en betaalbaarder organiseren. Zodat Hagenaars langer en aangenamer zelfstandig kunnen blijven wonen.</p>
        <h4>De kwaliteit van leven verbeteren</h4>
        <p>Met onze opgave Zorg en Innovatie richten wij ons op technologische vernieuwing voor mensen met een zorg- of ondersteuningsvraag, van jong tot oud. Technologie is hierbij een middel om de kwaliteit van leven te verbeteren, het is nooit een doel op zich.</p>
        <h4>Samen met inwoners, organisaties en bedrijven</h4>
        <p>We werken nauw samen met inwoners, zorgorganisaties, kennisinstellingen en bedrijven in de techsector. Want technologie biedt kansen, maar alleen als die technologie bij mensen past, en mensen ermee om kunnen gaan. Daarom is een goede verbinding tussen het sociale domein en de wereld van technologie zo ongelooflijk belangrijk. In Den Haag werken wij daar hard aan. Zodat iedereen de zorg en ondersteuning blijft krijgen die hij of zij nodig heeft.</p>
        <p>Bekijk deze klikbare tekening om te zien hoe wij het sociale domein en de techwereld samenbrengen.</p>
        <img src={wethouder} alt="" className="rounded-full w-48 mb-4" />
        <p><strong>Kavita Parbhudayal</strong><br />Wethouder Zorg, Jeugd en Volksgezondheid</p>
        <Button tag="button" onClick={toggleModal} type="button" variant={variant}>Bekijk interactive</Button>
      </Modal>
    </TopicContext.Provider>
  );
};
